import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";

/**
 * Career DNA™ reveal — presentation only.
 *
 * A Wrapped-style hand-off between "you answered" and "here's where you're
 * heading". It derives NOTHING: every line is passed in from the values the
 * results layer already computed. No scores, no percentages, no salary.
 */

export interface DnaCard {
  /** Small label above the statement. */
  eyebrow: string;
  /** The one big line. Kept short on purpose. */
  headline: string;
  /** One supporting sentence, second person, present tense. */
  body?: string;
}

export interface DnaRevealProps {
  cards: DnaCard[];
  /** Label on the final action. */
  ctaLabel: string;
  onDone: () => void;
}

export function DnaReveal({ cards, ctaLabel, onDone }: DnaRevealProps) {
  const [i, setI] = useState(0);
  const reduce = useReducedMotion();
  const last = i >= cards.length - 1;

  const next = useCallback(() => {
    if (last) onDone();
    else setI((n) => n + 1);
  }, [last, onDone]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); setI((n) => Math.max(0, n - 1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next]);

  const card = cards[i];

  return (
    <div className="min-h-dvh bg-background text-foreground relative overflow-hidden">
      {/* ambient depth, not decoration for its own sake */}
      <div aria-hidden className="pointer-events-none absolute -top-40 -left-24 h-80 w-80 rounded-full bg-primary/20 blur-[120px]" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-24 h-80 w-80 rounded-full bg-ai/15 blur-[120px]" />

      <div className="relative z-10 min-h-dvh max-w-2xl mx-auto px-6 pt-6 pb-10 flex flex-col">
        {/* progress */}
        <div className="flex items-center gap-2" role="group" aria-label="Reveal progress">
          {cards.map((c, n) => (
            <span
              key={c.headline}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                n <= i ? "bg-primary" : "bg-border"
              }`}
            />
          ))}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Card {i + 1} of {cards.length}
        </div>

        <div className="flex-1 grid place-items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.98 }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -16 }}
              transition={{ duration: reduce ? 0.15 : 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="calm-card w-full p-7 md:p-10 text-center"
              aria-live="polite"
            >
              <div className="text-xs uppercase tracking-[0.24em] text-primary">{card.eyebrow}</div>
              <h1 className="mt-4 font-display text-3xl md:text-5xl leading-tight font-medium">
                {card.headline}
              </h1>
              {card.body && (
                <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
                  {card.body}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="space-y-3">
          <button
            onClick={next}
            className="w-full min-h-[52px] rounded-2xl bg-primary text-primary-foreground
              font-medium inline-flex items-center justify-center gap-2
              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
              focus-visible:ring-offset-background"
          >
            {last ? ctaLabel : "Next"} <ArrowRight className="h-4 w-4" />
          </button>
          {!last && (
            <button
              onClick={onDone}
              className="w-full min-h-[44px] rounded-2xl text-sm text-muted-foreground hover:text-foreground"
            >
              Skip to my results
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
